"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  CheckCircle,
  Star,
  Crop,
  X,
  Check,
  Loader2,
  Tag,
  Building2,
  IdCard
} from "lucide-react";

import useTemplateBase, {
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import openHouseBadge from "../../assets/star_red.png";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

interface PropertyList {
  id: number;
  list_name: string;
}

interface Template5FlyerData {
  tag: string;
  price: number | null;
  headline: string;
  address: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  area?: number | null;
  description: string;
  features: { id?: number; feature_name: string }[];
  additionalFeatures: { id?: number; feature_name: string }[];
  agentName: string;
  agentPhone: string;
  agentEmail: string;
  heroImage: string;
  secondaryImage?: string;
  photos?: string[];
  photo2?: string;
  photo3?: string;
  list_name?: string;
}

interface Template5Props {
  property?: any;
  propertyLists?: PropertyList[];
  keyFeatures?: any[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<Template5FlyerData>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string;
  readOnly?: boolean;
}

const Template5: React.FC<Template5Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
  selectedList: viewSelectedList,
  readOnly = false,
}) => {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  const mainPhotoRef = useRef<HTMLInputElement>(null);
  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const photo2ContainerRef = useRef<HTMLDivElement | null>(null);
  const photo3ContainerRef = useRef<HTMLDivElement | null>(null);

  const [isCustomList, setIsCustomList] = useState<boolean>(false);
  const [customListName, setCustomListName] = useState<string>("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);

  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [boxPhoto2Res, setBoxPhoto2Res] = useState<string | null>(null);
  const [boxPhoto3Res, setBoxPhoto3Res] = useState<string | null>(null);

  const [editingCustom5, setEditingCustom5] = useState(false);
  const [customFields5, setCustomFields5] = useState<
    { key: string; value: string }[]
  >([]);

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    updatePropertyList,
    agentData,
    handleInputChange,
    handleFileChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePhotoAtIndex,
    removePhotoAtIndex,
    uploadCroppedPhoto,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  useEffect(() => {
    const existingCustomData =
      property?.propertyListType?.custom_template_data?.template5 || [];
    setCustomFields5(
      Array.isArray(existingCustomData) ? existingCustomData : []
    );
  }, [property?.propertyListType?.custom_template_data?.template5]);

  useEffect(() => {
    const measure = () => {
      try {
        if (heroContainerRef.current) {
          const w = Math.round(heroContainerRef.current.clientWidth);
          const h = Math.round(heroContainerRef.current.clientHeight);
          setBoxHeroRes(`${w}×${h}`);
        }
        if (photo2ContainerRef.current) {
          const w = Math.round(photo2ContainerRef.current.clientWidth);
          const h = Math.round(photo2ContainerRef.current.clientHeight);
          setBoxPhoto2Res(`${w}×${h}`);
        }
        if (photo3ContainerRef.current) {
          const w = Math.round(photo3ContainerRef.current.clientWidth);
          const h = Math.round(photo3ContainerRef.current.clientHeight);
          setBoxPhoto3Res(`${w}×${h}`);
        }
      } catch {
        // ignore measurement errors
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (heroContainerRef.current) ro.observe(heroContainerRef.current);
      if (photo2ContainerRef.current) ro.observe(photo2ContainerRef.current);
      if (photo3ContainerRef.current) ro.observe(photo3ContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ??
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Just Listed";

  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Just Listed" },
        { id: 2, list_name: "Open House" },
        { id: 3, list_name: "New Price" },
        { id: 4, list_name: "Luxury Collection" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "Just Listed";

  const addCustomField5 = () => {
    if (readOnly) return;
    setCustomFields5((p) => [...p, { key: "", value: "" }]);
  };

  const updateCustomField5 = (i: number, field: "key" | "value", v: string) => {
    if (readOnly) return;
    setCustomFields5((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };

  const removeCustomField5 = (i: number) => {
    if (readOnly) return;
    setCustomFields5((p) => p.filter((_, idx) => idx !== i));
  };

  const saveCustomForTemplate5 = async () => {
    if (readOnly) return;
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData(
          "template5",
          customFields5.filter((f) => f.key || f.value)
        );
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template5: customFields5,
              },
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
        }
      }
      setEditingCustom5(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (readOnly) return;
    if (!property?.id) return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "Special styling enabled"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save choice",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  const saveCustomList = async () => {
    if (readOnly) return;

    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);

      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      setIsCustomList(false);
      setCustomListName("");

      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and selected`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "heroImage" | "photo2" | "photo3"
  ) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (field === "heroImage") {
      // Capture this freshly-picked photo as the untouched "original",
      // independently of the shared handleFileChange call below (which only
      // persists the displayed "image" field), so Edit Image always has a
      // full source to re-crop from later. Mirrors Template1.tsx.
      const reader = new FileReader();
      reader.onload = () => {
        setOriginalHeroImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      if (property?.id) {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("original_image", file);
        axiosInstance
          .patch(`${endpoints.properties}${property.id}/`, formData, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          })
          .catch((err) => {
            console.error("Failed to save original image:", err);
          });
      }

      await handleFileChange(e);
      return;
    }

    // Clear for re-selection of the same file
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;

      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;
        const expectedResolution =
          field === "photo2" ? boxPhoto2Res : boxPhoto3Res;

        if (expectedResolution) {
          const parts = expectedResolution.split("×").map((s) => Number(s));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            if (w !== rw || h !== rh) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${w}×${h}. Recommended: ${expectedResolution}. If possible, upload this resolution for best results.`,
                variant: "default",
              });
            }
          }
        }
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      setFlyerData((prev) => ({ ...prev, [field]: dataUrl }));

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const uploadRes = await axiosInstance.post(
          endpoints.mls_uploads,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (uploadRes?.data?.url) {
          const imageUrl = uploadRes.data.url;

          await updatePhotoAtIndex(imageUrl, field as "photo2" | "photo3");

          toast({ title: "Success", description: "Image uploaded and saved" });
        } else {
          throw new Error("No URL returned from upload");
        }
      } catch (err) {
        console.error("Failed to upload secondary image:", err);
        toast({
          title: "Upload Error",
          description: "Failed to upload image to server",
          variant: "destructive",
        });

        setFlyerData((prev) => ({ ...prev, [field]: "" }));
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 400;
  const EDIT_CROP_H = 237; // matches the ~760x450 hero box aspect ratio

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  // ─── Edit Image for secondary/gallery photos (same non-destructive pattern
  // as the hero photo above, reusing the shared updatePhotoAtIndex/
  // uploadCroppedPhoto helpers from TemplateBase so original-capture stays
  // centralized) ──
  const SECONDARY_CROP_W = 330;
  const SECONDARY_CROP_H = 280; // matches the ~330x280 secondary-photo box aspect ratio

  const [secondaryCropModal, setSecondaryCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    field: "photo2" | "photo3" | null;
  }>({ open: false, imageUrl: "", field: null });

  const openEditSecondaryPhoto = async (
    field: "photo2" | "photo3",
    currentSrc: string
  ) => {
    if (readOnly || !currentSrc) return;

    // Keyed by slot name ("photo2"/"photo3"), not array position — see
    // TemplateBase.tsx's updatePhotoAtIndex for why.
    let originalSource = (flyerData.original_photos && flyerData.original_photos[field]) || "";

    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_photos?.[field] || "";
      } catch (err) {
        console.error("Failed to check for existing original photo:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal by re-saving the current photo
      // through updatePhotoAtIndex (captureOriginal defaults to true), so
      // this and every future edit has a stable, full source to re-crop
      // from instead of the current display.
      originalSource = currentSrc;
      if (!currentSrc.startsWith("data:")) {
        updatePhotoAtIndex(currentSrc, field).catch((err) => {
          console.error("Failed to seed original photo:", err);
        });
      }
    }

    setSecondaryCropModal({
      open: true,
      imageUrl: getEditableImageUrl(originalSource),
      field,
    });
  };

  const handleSecondaryEditConfirm = async (croppedDataUrl: string) => {
    const field = secondaryCropModal.field;
    if (!field) return;
    try {
      const newUrl = await uploadCroppedPhoto(croppedDataUrl);
      await updatePhotoAtIndex(newUrl, field, { captureOriginal: false });
      toast({ title: "Success", description: "Photo updated" });
    } catch (err) {
      console.error("Failed to save edited photo:", err);
      toast({
        title: "Error",
        description: "Failed to save edited photo",
        variant: "destructive",
      });
    } finally {
      setSecondaryCropModal({ open: false, imageUrl: "", field: null });
    }
  };

  const handleSecondaryEditCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "", field: null });
  };

  const getPhotoSrc = (
    field: "photo2" | "photo3",
    index: number
  ): string => {
    const fieldValue = flyerData[field];
    if (fieldValue && typeof fieldValue === "string" && fieldValue.trim() !== "") {
      return fieldValue;
    }

    if (field === "photo2") {
      const secondary = flyerData.secondaryImage;
      if (secondary && typeof secondary === "string" && secondary.trim() !== "") {
        return secondary;
      }
    }

    if (Array.isArray(flyerData.photos) && flyerData.photos[index]) {
      const photoFromArray = flyerData.photos[index];
      if (photoFromArray && typeof photoFromArray === "string") {
        return photoFromArray;
      }
    }

    if (property && Array.isArray(property.photos) && property.photos[index]) {
      const propertyPhoto = property.photos[index];
      if (propertyPhoto && typeof propertyPhoto === "string") {
        return propertyPhoto;
      }
    }

    return "";
  };

  const handleRemovePhoto = async (field: "photo2" | "photo3") => {
    if (readOnly) return;
    await removePhotoAtIndex(field);
    toast({
      title: "Image Removed",
      description: "Property image has been removed",
    });
  };

  const photo2Src = getPhotoSrc("photo2", 0);
  const photo3Src = getPhotoSrc("photo3", 1);
  return (
    <div
      className="overflow-x-auto w-full"
      style={{
        WebkitOverflowScrolling: "touch",
        transform: "rotateX(180deg)",
      }}
    >
      <div
        className="bg-white rounded-none shadow-none overflow-hidden font-arial relative border-0 mx-auto block "
        style={{
          minWidth: "760px",
          transform: "rotateX(180deg)",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          borderRadius: "20px",
        }}
      >
        {/* Custom Fields Editor Modal */}
        {editingCustom5 && !readOnly && (
          <div className="absolute right-6 top-20 z-50 bg-white p-4 rounded-lg shadow-xl w-80 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <strong className="text-gray-900">Custom Fields</strong>
              <div className="flex gap-2">
                <button
                  onClick={addCustomField5}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Add
                </button>
                <button
                  onClick={() => setEditingCustom5(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {customFields5.map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <input
                    value={f.key}
                    onChange={(e) =>
                      updateCustomField5(idx, "key", e.target.value)
                    }
                    placeholder="Key"
                    className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                  />
                  <input
                    value={f.value}
                    onChange={(e) =>
                      updateCustomField5(idx, "value", e.target.value)
                    }
                    placeholder="Value"
                    className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                  />
                  <button
                    onClick={() => removeCustomField5(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveCustomForTemplate5}
                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white p-6 md:p-8">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Star className="h-7 w-7 md:h-8 md:w-8 flex-shrink-0" />
              <h2 className="text-xl md:text-2xl font-bold">
                {agentData?.company || "PREMIER REALTY GROUP"}
              </h2>
            </div>

            <div className="flex items-center justify-end ml-auto">
              {readOnly ? (
                <div
                  className="
        w-[300px]
        h-[50px]
        flex items-center justify-center
        bg-white/20 backdrop-blur-sm
        text-white
        rounded-full
        font-bold
        text-base
        border border-white/30
      "
                >
                  <span className="truncate px-4">
                    {listSelectValue || "Just Listed"}
                  </span>
                </div>
              ) : isCustomList ? (
                <div
                  className="
        w-[300px]
        h-[50px]
        flex items-center gap-2
        bg-white/20 backdrop-blur-sm
        text-white
        rounded-full
        border border-white/30
        px-4
      "
                >
                  <input
                    type="text"
                    value={customListName}
                    onChange={(e) =>
                      setCustomListName(e.target.value.slice(0, 20))
                    }
                    placeholder="Custom tag"
                    maxLength={20}
                    className="
          flex-1
          bg-transparent
          text-white
          placeholder-white/60
          outline-none
          border-none
          font-bold
        "
                    autoFocus
                    disabled={isSavingCustomList}
                  />

                  <button
                    onClick={saveCustomList}
                    disabled={!customListName.trim() || isSavingCustomList}
                    className="p-1.5 bg-white/20 rounded-full hover:bg-white/30"
                  >
                    {isSavingCustomList ? (
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 text-white" />
                    )}
                  </button>

                  <button
                    onClick={cancelCustomList}
                    className="p-1.5 bg-white/20 rounded-full hover:bg-white/30"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ) : (
                <select
                  value={listSelectValue}
                  onChange={(e) => handleListSelection(e.target.value)}
                  className="
        w-[300px]
        h-[50px]
        bg-white/20 backdrop-blur-sm
        text-white
        rounded-full
        font-bold
        text-base
        border border-white/30
        px-6
        focus:outline-none
      "
                >
                  <option value="" className="text-gray-900">
                    -- Select List --
                  </option>

                  {allLists
                    .filter((list) => list.list_name !== "Open House")
                    .map((list) => (
                      <option
                        key={list.id}
                        value={list.list_name}
                        className="text-gray-900"
                      >
                        {list.list_name}
                      </option>
                    ))}

                  <option
                    value="__custom__"
                    className="text-gray-900"
                  >
                    + Add Custom Tag
                  </option>
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="relative bg-gradient-to-b from-gray-50 to-white">
          <div
            ref={heroContainerRef}
            className="relative h-[450px] md:h-[450px] overflow-hidden group"
            style={{
              height: '450px',
              minHeight: '450px',
              maxHeight: '450px',
            }}
          >
            {!readOnly && (
              <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                  {boxHeroRes ? (
                    <div className="leading-snug">
                      <div className="font-semibold">Required image resolution: {boxHeroRes}</div>
                      <div className="mt-1 text-[11px]">Please upload this resolution for best results.</div>
                    </div>
                  ) : (
                    <div className="leading-snug">For best results, upload a photo that fits the hero box.</div>
                  )}
                </div>
              </div>
            )}
            {isOpenHouseConfirmed && (
              <div className="absolute top-4 right-6 md:top-0 md:right-6 z-20 " style={{ marginRight: '-30px', marginTop: '-0px' }}>
                <div className="relative w-24 md:w-32">
                  <img
                    src={openHouseBadge}
                    alt="Open House"
                    className="w-full drop-shadow-xl"
                  />
                  <span
                    className="absolute inset-0 flex items-center justify-center
                   text-white font-extrabold text-xs md:text-sm
                   tracking-wide text-center pointer-events-none"
                  >
                    OPEN
                    <br />
                    HOUSE
                  </span>
                </div>
              </div>
            )}

            {flyerData.heroImage ? (
              <>
                <img
                  src={flyerData.heroImage}
                  alt="Main Property"
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                {!readOnly && (
                  <div
                    onClick={() => mainPhotoRef.current?.click()}
                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center cursor-pointer"
                  >
                    <div className="text-center text-white transform scale-90 group-hover:scale-100 transition-transform">
                      <Upload className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3" />
                      <p className="text-xl md:text-2xl font-bold">
                        Change Main Photo
                      </p>
                      <p className="text-sm md:text-base mt-2 text-white/80">
                        Click to upload new image
                      </p>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditImage();
                        }}
                        className="mt-4 bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Crop className="h-4 w-4 mr-2" />
                        Edit Image
                      </Button>
                    </div>
                  </div>
                )}

                <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 z-10">
                  {readOnly ? (
                    <p className="text-5xl md:text-7xl lg:text-8xl font-black text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.95)] leading-none">
                      {flyerData.price
                        ? `$${Number(flyerData.price).toLocaleString()}`
                        : "$0"}
                    </p>
                  ) : (
                    <>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={flyerData.price ?? ""}
                        onChange={(e) => {
                          const num = e.target.value.replace(/[^0-9]/g, "");
                          handleInputChange("price", num ? Number(num) : null);
                        }}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        placeholder="Enter price"
                      />

                      <div
                        className="cursor-pointer select-none"
                        onClick={() => {
                          const hiddenInput = document.querySelector(
                            'input[placeholder="Enter price"]'
                          ) as HTMLInputElement;
                          hiddenInput?.focus();
                          hiddenInput?.click();
                        }}
                      >
                        {flyerData.price ? (
                          <p className="text-5xl md:text-7xl lg:text-8xl font-black text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.95)] leading-none">
                            ${Number(flyerData.price).toLocaleString()}
                          </p>
                        ) : (
                          <p className="text-5xl md:text-7xl lg:text-8xl font-black text-white/50 drop-shadow-[0_4px_30px_rgba(0,0,0,0.95)]">
                            $0,000,000
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : !readOnly ? (
              <label
                htmlFor="main-photo-t5"
                className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400 hover:text-blue-600 cursor-pointer transition-all duration-300"
              >
                <ImageIcon className="h-20 w-20 md:h-28 md:w-28 mb-4" />
                <p className="text-2xl md:text-3xl font-bold">
                  Upload Main Photo
                </p>
                <p className="text-base md:text-lg mt-2">
                  Click or drag image here
                </p>
              </label>
            ) : (
              <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400">
                <ImageIcon className="h-20 w-20 md:h-28 md:w-28 mb-4" />
                <p className="text-2xl md:text-3xl font-bold">No Image</p>
              </div>
            )}
          </div>

          {/* Two Smaller Photos Below */}
          <div className="w-full px-6 md:px-10 mt-8">
            <div
              className="flex flex-row gap-4"
              style={{
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {/* Photo 2 */}
              <div
                ref={photo2ContainerRef}
                className="relative rounded-2xl overflow-hidden shadow-2xl group bg-white p-2"
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  height: "280px",
                }}
              >
                {!readOnly && (
                  <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[280px]">
                      {boxPhoto2Res ? (
                        <div className="leading-snug">
                          <div className="font-semibold">Required image resolution: {boxPhoto2Res}</div>
                          <div className="mt-1 text-[11px]">Please upload this resolution for best results.</div>
                        </div>
                      ) : (
                        <div className="leading-snug">For best results, upload a full-width interior photo.</div>
                      )}
                    </div>
                  </div>
                )}
                {photo2Src ? (
                  <>
                    <img
                      src={photo2Src}
                      alt="Interior"
                      className="w-full h-full object-cover object-center rounded-xl transition-transform group-hover:scale-110 duration-500"
                    />
                    {!readOnly && (
                      <div className="absolute inset-2 bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl transition-all duration-300 flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => photo2Ref.current?.click()}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Change
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openEditSecondaryPhoto("photo2", photo2Src)}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Crop className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </>
                ) : !readOnly ? (
                  <label
                    htmlFor="photo2-t5"
                    className="flex flex-col items-center justify-center h-full text-gray-400 hover:text-blue-600 cursor-pointer transition-all duration-300 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl"
                  >
                    <ImageIcon className="h-14 w-14 md:h-16 md:w-16 mb-3" />
                    <p className="font-semibold text-base md:text-lg">
                      Add Interior Photo
                    </p>
                    <p className="text-xs md:text-sm text-gray-400 mt-1">
                      Click to upload
                    </p>
                  </label>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
                    <ImageIcon className="h-14 w-14 md:h-16 md:w-16 mb-3" />
                    <p className="font-semibold text-base md:text-lg">No Image</p>
                  </div>
                )}
              </div>

              {/* Photo 3 */}
              <div
                ref={photo3ContainerRef}
                className="relative rounded-2xl overflow-hidden shadow-2xl group bg-white p-2"
                style={{ flex: "1 1 0", minWidth: 0, height: "280px" }}
              >
                {!readOnly && (
                  <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[280px]">
                      {boxPhoto3Res ? (
                        <div className="leading-snug">
                          <div className="font-semibold">Required image resolution: {boxPhoto3Res}</div>
                          <div className="mt-1 text-[11px]">Please upload this resolution for best results.</div>
                        </div>
                      ) : (
                        <div className="leading-snug">For best results, upload a full-width exterior photo.</div>
                      )}
                    </div>
                  </div>
                )}
                {photo3Src ? (
                  <>
                    <img
                      src={photo3Src}
                      alt="Exterior"
                      className="w-full h-full object-cover object-center rounded-xl transition-transform group-hover:scale-110 duration-500"
                    />
                    {!readOnly && (
                      <div className="absolute inset-2 bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl transition-all duration-300 flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => photo3Ref.current?.click()}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Change
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openEditSecondaryPhoto("photo3", photo3Src)}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Crop className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </>
                ) : !readOnly ? (
                  <label
                    htmlFor="photo3-t5"
                    className="flex flex-col items-center justify-center h-full text-gray-400 hover:text-blue-600 cursor-pointer transition-all duration-300 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl"
                  >
                    <ImageIcon className="h-14 w-14 md:h-16 md:w-16 mb-3" />
                    <p className="font-semibold text-base md:text-lg">
                      Add Exterior Photo
                    </p>
                    <p className="text-xs md:text-sm text-gray-400 mt-1">
                      Click to upload
                    </p>
                  </label>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
                    <ImageIcon className="h-14 w-14 md:h-16 md:w-16 mb-3" />
                    <p className="font-semibold text-base md:text-lg">No Image</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!readOnly && (
            <>
              <input
                id="main-photo-t5"
                ref={mainPhotoRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "heroImage")}
              />
              <input
                id="photo2-t5"
                ref={photo2Ref}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "photo2")}
              />
              <input
                id="photo3-t5"
                ref={photo3Ref}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "photo3")}
              />
              <ImageCropModal
                open={cropModal.open}
                imageUrl={cropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={EDIT_CROP_W}
                cropH={EDIT_CROP_H}
                onConfirm={handleEditImageConfirm}
                onCancel={handleEditImageCancel}
              />
              <ImageCropModal
                open={secondaryCropModal.open}
                imageUrl={secondaryCropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={SECONDARY_CROP_W}
                cropH={SECONDARY_CROP_H}
                onConfirm={handleSecondaryEditConfirm}
                onCancel={handleSecondaryEditCancel}
              />
            </>
          )}
        </div>

        <div className="px-6 md:px-10 py-8 md:py-12 space-y-10 md:space-y-14">
          <div className="text-center space-y-4">
            {readOnly ? (
              <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center">
                {flyerData.headline || "Modern Masterpiece in Prime Location"}
              </p>
            ) : (
              <textarea
                value={flyerData.headline || ""}
                onChange={(e) => handleInputChange("headline", e.target.value)}
                placeholder="Modern Masterpiece in Prime Location"
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 bg-transparent border-none outline-none w-full text-center placeholder-gray-400 focus:placeholder-gray-300 transition-all"
              />
            )}
            <div className="flex items-center justify-center text-gray-600 gap-2">
              <MapPin className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0 text-blue-600" />
              {readOnly ? (
                <p className="text-base md:text-xl font-medium text-center">
                  {flyerData.address || "No address provided"}
                </p>
              ) : (
                <input
                  type="text"
                  value={flyerData.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="123 Ocean Avenue, Malibu, CA 90265"
                  className="text-base md:text-xl font-medium bg-transparent border-none outline-none text-center placeholder-gray-400 max-w-2xl"
                />
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {[
              { icon: Bed, label: "Bedrooms", field: "bedrooms" as const },
              { icon: Bath, label: "Bathrooms", field: "bathrooms" as const },
              { icon: Square, label: "Sq Ft", field: "sqft" as const },
            ].map(({ icon: Icon, label, field }) => (
              <div
                key={field}
                className="text-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-2xl p-6 md:p-8 border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden min-w-0"
              >
                <Icon className="h-12 w-12 md:h-14 md:w-14 text-blue-600 mx-auto mb-4" />
                {readOnly ? (
                  <p
                    className={`font-black text-gray-900 text-center w-full leading-tight ${String(flyerData[field] ?? 0).length > 5
                      ? "text-2xl md:text-3xl"
                      : String(flyerData[field] ?? 0).length > 3
                        ? "text-3xl md:text-4xl"
                        : "text-4xl md:text-5xl"
                      }`}
                  >
                    {flyerData[field] ?? 0}
                  </p>
                ) : (
                  <input
                    type="number"
                    min="0"
                    value={flyerData[field] ?? ""}
                    onChange={(e) =>
                      handleInputChange(
                        field,
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className={`font-black text-gray-900 bg-transparent border-none outline-none w-full text-center [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none leading-tight ${String(flyerData[field] ?? "").length > 5
                      ? "text-2xl md:text-3xl"
                      : String(flyerData[field] ?? "").length > 3
                        ? "text-3xl md:text-4xl"
                        : "text-4xl md:text-5xl"
                      }`}
                    placeholder="0"
                  />
                )}
                <p className="text-base md:text-lg text-gray-600 mt-3 font-medium">
                  {label}
                </p>
              </div>
            ))}
          </div>

          {/* Description */}
          <div className="text-center max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Property Overview
            </h3>
            {readOnly ? (
              <p className="text-base md:text-xl text-gray-700 leading-relaxed text-center whitespace-pre-wrap">
                {flyerData.description || "No description provided."}
              </p>
            ) : (
              <textarea
                value={flyerData.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="This exceptional residence offers unparalleled luxury with sweeping ocean views..."
                rows={5}
                className="w-full text-base md:text-xl text-gray-700 leading-relaxed bg-transparent border-none outline-none resize-none text-center placeholder-gray-400 focus:placeholder-gray-300"
              />
            )}
          </div>

          {/* Features */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-pink-600 mb-8 md:mb-10 flex items-center justify-center gap-3">
              <CheckCircle className="h-8 w-8 md:h-10 md:w-10 text-pink-600" />
              <span>Luxury Features & Amenities</span>
              {!readOnly && (
                <span className="text-sm font-normal text-gray-500">
                  ({flyerData.features.length}/10)
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {flyerData.features.map((feature: any, index: number) => (
                <div
                  key={feature.id ?? index}
                  className="flex items-center bg-gradient-to-r from-blue-50 to-pink-50 rounded-xl p-4 md:p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 group min-w-0"
                >
                  <CheckCircle className="h-6 w-6 md:h-7 md:w-7 text-pink-600 mr-3 md:mr-4 flex-shrink-0" />
                  {readOnly ? (
                    <span className="text-base md:text-lg font-medium text-gray-800 min-w-0 break-words">
                      {feature.feature_name || "Feature"}
                    </span>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={feature.feature_name}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        onBlur={() => handleFeatureBlur(index)}
                        maxLength={30}
                        placeholder="Home Theater (max 30 chars)..."
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-base md:text-lg font-medium text-gray-800 placeholder-gray-400"
                      />
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(index);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-2xl font-bold ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Delete feature"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              ))}

              {!readOnly && flyerData.features.length < 10 && (
                <Button
                  onClick={addFeature}
                  variant="outline"
                  className="col-span-2 h-14 md:h-16 text-base md:text-lg font-medium border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300"
                >
                  + Add Another Feature
                </Button>
              )}

              {!readOnly && flyerData.features.length >= 10 && (
                <p className="col-span-full text-center text-sm text-gray-500 italic">
                  Maximum of 10 features reached
                </p>
              )}
            </div>
          </div>

          {/* ── AGENT SPOTLIGHT ── */}
          {/*
          Responsive strategy (v4 — mobile-first):
          • All sizes  : flex column — avatars row centred, info block below centred.
          • ≥520px      : switch to side-by-side row (avatars left, info right).
          We use an inline <style> block so the breakpoint works regardless of
          whether Tailwind's `sm:` (640px) prefix is available, and regardless
          of the parent container width.  Avatar size is fixed at 72px so even
          on the narrowest phones (320px) two circles + gap never exceed ~160px.
        */}
          <style>{`
          .agent-spotlight-inner {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            gap: 1.25rem;
          }
          .agent-avatars {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 0.75rem;
            flex-shrink: 0;
          }
          .agent-info {
            width: auto;
            text-align: left;
            overflow: hidden;
            flex: 1;
            min-width: 0;
          }
          .agent-contact-row {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 0.5rem;
          }
          .agent-contact-text {
            overflow-wrap: break-word;
            word-break: break-all;
            min-width: 0;
            flex: 1;
            text-align: left;
          }
        `}</style>

          <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-3 sm:p-3 md:p-3 border border-gray-100">


            <div className="flex flex-row items-center gap-5 md:gap-6">

              {/* Agent Avatar - LEFT */}
              <div
                className="rounded-xl overflow-hidden  flex-shrink-0"
                style={{ width: 150, height: 180 }}
              >
                {agentData?.avatar ? (
                  <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <ImageIcon className="h-7 w-7 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Agent Info - CENTER */}
              <div className="flex-1 min-w-0 flex flex-col justify-center items-center">

                {/* Name */}
                <p className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-snug flex justify-center items-center">
                  {flyerData.agentName || "Agent Name"}
                </p>

                {/* Contact rows */}
                <div className="space-y-2 sm:space-y-3 w-full flex flex-col justify-center items-center">

                  {/* Phone */}
                  <div className="inline-flex items-center gap-1.5 mx-auto">
                    <Phone className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-gray-700">
                      {flyerData.agentPhone || "N/A"}
                    </span>
                  </div>

                  {/* Email */}
                  <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                    <Mail className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium underline text-gray-700">
                      {/* {flyerData.agentEmail || "N/A"} */}
                        Click here to email Me
                    </span>
                  </div>


                  {/* Company */}
                  <div className="inline-flex items-center gap-1.5 mx-auto">
                    <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-gray-700">
                      {flyerData.companyName || agentData?.company || "N/A"}
                    </span>
                  </div>

                  {/* License Number */}
                  {agentData?.license_number && (
                    <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                      <IdCard className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="text-sm sm:text-base font-medium text-gray-700">
                        License #: {agentData.license_number}
                      </span>
                    </div>
                  )}

                </div>
              </div>
              {/* Agent Info closes */}

              {/* Company Logo - RIGHT */}
              <div
                className="rounded-xl overflow-hidden  flex items-center justify-center flex-shrink-0"
                style={{ width: 180, height: 180 }}
              >
                {agentData?.company_logo ? (
                  <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-gray-400 text-center px-1 leading-tight">Logo</span>
                )}
              </div>

            </div>
          </div>
          {/* ── END AGENT SPOTLIGHT ── */}
        </div>

        {/* Custom fields display */}
        {property?.propertyListType?.custom_template_data?.template5 &&
          Array.isArray(
            property.propertyListType.custom_template_data.template5
          ) &&
          property.propertyListType.custom_template_data.template5.length > 0 && (
            <div className="px-6 md:px-10 py-6">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-sm text-gray-800 mb-2">
                  Custom Fields
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                  {property.propertyListType.custom_template_data.template5.map(
                    (f: any, i: number) => (
                      <div key={i} className="flex justify-between gap-2">
                        <span className="font-medium break-words">{f.key}</span>
                        <span className="break-words text-right">{f.value}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Template5;
